import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { PropTypes } from 'prop-types';
import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import styled from 'styled-components';

import { PageContainer } from '@ge/components/containers';
import { ConfirmationDialog } from '@ge/components/modal';
import { DynamicTable } from '@ge/components/table';
import { useColumnState } from '@ge/components/table/use-column-state';
import { useTableFactories } from '@ge/components/table/use-table-factories';
import { useCRTrackerLockPeriodTable } from '@ge/feat-reporting/data-hooks/use-cr-tracker-lock-period-table';
import {
  Capability,
  DateTimeFormats,
  PermissionScope,
  Placeholders,
  QueryKey,
} from '@ge/models/constants';
import { useAuth } from '@ge/shared/data-hooks';
import { useFilterDefs } from '@ge/shared/hooks';
import {
  CRTrackerColumnDefs,
  CRTrackerColumns,
} from '@ge/shared/models/table-col-defs/cr-tracker-table-cols';
import { getPreferredUserName } from '@ge/shared/services/auth';
import { updateCRTrackerLockPeriodApi } from '@ge/shared/services/cr_tracker_lock_month';
import { killEventPropagation } from '@ge/shared/util';

import { defaultCrTrackerCols } from '../../models/cr-tracker-table-cols-default';

import CRContractsLockPopUp from './cr-contracts-Lock-pop-up';

dayjs.extend(utc);
dayjs.extend(timezone);

const ModalMessage = styled.p`
  margin: 6px 0px;
  width: 300px;
  font-size: 14px;
`;

const CR_TRACKER_FILTER_STATE_ID = 'analyze.dashboard.cr_tracker';
const CR_TRACKER_SORT_STATE_ID = 'analyze.dashboard.cr_tracker';

const CRContractTable = ({ contractId }) => {
  const { t, ready } = useTranslation(['analyze.cr-tracker'], {
    useSuspense: false,
  });

  const { isAuthorized } = useAuth();
  const [currentSearch, setCurrentSearch] = useState({});
  const [displayPopUp, setDisplayPopUp] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const checkedRow = useRef(null);
  const queryClient = useQueryClient();
  const userInfo = getPreferredUserName();

  const {
    filterSequence,
    filterDefs,
    onChange: setFilterDefs,
  } = useFilterDefs({
    columnDefs: CRTrackerColumnDefs,
    stateId: CR_TRACKER_FILTER_STATE_ID,
  });

  const {
    visibleCols: visibleCRTrackerCols,
    sortDirection: crTrackerSortDirection,
    sortMetric: crTrackerSortMetric,
    updateSortMetric: updateCRTrackerSortMetric,
  } = useColumnState({
    columnDefs: CRTrackerColumnDefs,
    defaultCols: defaultCrTrackerCols,
    sortStateId: CR_TRACKER_SORT_STATE_ID,
  });

  const {
    data: crTrackerData,
    isLoading: isCRTrackerLoading,
    filterValues,
  } = useCRTrackerLockPeriodTable({
    filters: filterDefs,
    search: currentSearch,
    sortMetric: crTrackerSortMetric,
    sortDirection: crTrackerSortDirection,
    filterSequence,
    contractId,
  });

  const sortedDirection = useCallback(
    (metric) => (metric === crTrackerSortMetric ? crTrackerSortDirection : ''),
    [crTrackerSortMetric, crTrackerSortDirection],
  );

  const handleFilterChange = useCallback((_, key, value) => {
    setCurrentSearch({ key, value });
  }, []);

  const cellValueMapFn = useCallback((row) => {
    return {
      [CRTrackerColumns.MONTH]: row?.month_date || Placeholders.DASH,
      [CRTrackerColumns.CONTRACTUAL_AVAILABILITY]:
        row?.contractual_availability || Placeholders.DASH,
      [CRTrackerColumns.MALS_ADDED]: row?.mals_added || Placeholders.DASH,
      [CRTrackerColumns.MALS_APPROVED]: row?.mals_approved || Placeholders.DASH,
      [CRTrackerColumns.MALS_NOT_VALIDATED]: row?.mals_not_validated || Placeholders.DASH,
      [CRTrackerColumns.MALS_REJECTED]: row?.mals_rejected || Placeholders.DASH,
      [CRTrackerColumns.STATUS_UPDATE]: row?.status_after_lock || Placeholders.DASH,
      [CRTrackerColumns.MALS_SUBMISSION_DATE]: row?.mals_after_lock || Placeholders.DASH,
      [CRTrackerColumns.LOCK_DONE_BY_SSO]: row?.locked_by || Placeholders.DASH,
      [CRTrackerColumns.LOCK_MONTH_DATE]: row?.lock_date
        ? dayjs.unix(row?.lock_date).tz(row?.timezone).format(DateTimeFormats.YEAR_MONTH_DAY)
        : Placeholders.DASH,
    };
  }, []);

  const isAuthorizedToApproveLock = isAuthorized({
    capabilities: [
      {
        capability: Capability.CR_TRACKER_LOCK_PERIOD,
        scopes: [PermissionScope.EDIT],
      },
    ],
  });

  const onModelOpen = () => {
    if (isAuthorizedToApproveLock) {
      setDisplayPopUp(true);
    }
  };

  const customCellFn = (columnKey, cellValue, value) => {
    switch (columnKey) {
      case CRTrackerColumns.LOCK_MONTH:
        return (
          <CRContractsLockPopUp value={value} onModelOpen={onModelOpen} checkedRow={checkedRow} />
        );
      default:
        return null;
    }
  };

  const handleFilterApply = useCallback(
    (_, columnKey, value) => {
      setFilterDefs(columnKey, value);
    },
    [setFilterDefs],
  );

  const [columnGroupFactory, columnFactory, cellFactory] = useTableFactories({
    t,
    columnDefs: CRTrackerColumnDefs,
    cellValueMapFn,
    customCellFn,
    sortAction: updateCRTrackerSortMetric,
    sortedDirection,
    filterValues,
    filters: filterDefs,
    onFilterApply: handleFilterApply,
    onFilterChange: handleFilterChange,
    defaultCellBorder: true,
  });

  const dynamicProps = {
    scrollable: true,
    columns: visibleCRTrackerCols,
    columnGroupFactory,
    columnFactory,
    cellFactory,
    sortAction: updateCRTrackerSortMetric,
    values: crTrackerData,
    onValueSelect: () => null,
    rowKeyProperty: 'id',
    isLoading: isCRTrackerLoading,
    noData: !isCRTrackerLoading && crTrackerData?.length <= 0,
  };

  const onModalClose = useCallback((e) => {
    killEventPropagation(e);
    setDisplayPopUp(false);
    setIsLoading(false);
    checkedRow.current = null;
  }, []);

  const handleModelConfirm = useCallback(
    async (e) => {
      killEventPropagation(e);
      try {
        setIsLoading(true);
        const selectedRow = checkedRow?.current;

        await updateCRTrackerLockPeriodApi({
          contractId,
          lockFlag: true,
          lockedBy: userInfo,
          monthDate: selectedRow?.month_date,
        });

        queryClient.setQueryData(
          [QueryKey.CR_TRACKER_LOCK_PERIOD_TABLE, contractId],
          (prevData) => {
            if (!prevData?.data?.length) {
              return prevData;
            }

            return {
              ...prevData,
              data: prevData.data.map((item) => {
                if (item?.month_date !== selectedRow?.month_date) {
                  return item;
                }

                return {
                  ...item,
                  lock_flag: true,
                  locked_by: userInfo,
                  lock_date: dayjs().unix(),
                };
              }),
            };
          },
        );
        onModalClose(e);
      } catch (error) {
        console.log('error', error);
        onModalClose(e);
      }
    },
    [contractId, onModalClose, queryClient, userInfo],
  );

  return (
    <>
      <ConfirmationDialog
        isOpen={displayPopUp}
        onConfirm={handleModelConfirm}
        onCancel={onModalClose}
        header={t('confirmation', 'Confirmation')}
        confirmLabel={t('yes,', 'Lock')}
        cancelLabel={t('cancel', 'Cancel')}
        isLoading={isLoading}
        loadingLabel={'Loading'}
      >
        <ModalMessage>
          {`Please remember that as per standard policy, a month should not be locked in case there are rejected MALs until we have a proper explanation and validation that customer’s point
          is correct and we as GEV are in line with the point.`}
        </ModalMessage>
      </ConfirmationDialog>
      <PageContainer i18nReady={ready}>
        <DynamicTable {...dynamicProps} />
      </PageContainer>
    </>
  );
};

export default CRContractTable;

CRContractTable.propTypes = {
  contractId: PropTypes.string.isRequired,
};
